import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import CoachChat from "@/components/CoachChat";
import AuthCallback from "@/components/AuthCallback";
import { AuthProvider, homeForRole, useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";
import Landing from "@/pages/Landing";
import PublicAssessment from "@/pages/PublicAssessment";
import SessionsPage from "@/pages/SessionsPage";
import PublicWorkouts from "@/pages/PublicWorkouts";
import Login from "@/pages/Login";
import CoachLogin from "@/pages/CoachLogin";
import AdminLogin from "@/pages/AdminLogin";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Coach from "@/pages/Coach";
import Admin from "@/pages/Admin";

const Loader = () => (
  <div className="min-h-screen bg-ink flex flex-col items-center justify-center gap-4">
    <Wordmark className="text-4xl" />
    <p className="text-zinc-500 text-xs uppercase tracking-[0.25em] animate-pulse">Loading…</p>
  </div>
);

function Protected({ children, role }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <Loader />;
  if (!user) {
    const loginPath = role === "admin" ? "/admin-login" : role === "coach" ? "/coach-login" : "/login";
    return <Navigate to={loginPath} replace state={{ from: location.pathname }} />;
  }
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (role && user.role !== role) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/assessment" element={<PublicAssessment />} />
      <Route path="/sessions" element={<SessionsPage />} />
      <Route path="/workouts" element={<PublicWorkouts />} />
      <Route path="/login" element={<Login />} />
      <Route path="/coach-login" element={<CoachLogin />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
      <Route path="/onboarding" element={<Protected role="member"><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected role="member"><Dashboard /></Protected>} />
      <Route path="/coach" element={<Protected role="coach"><Coach /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CoachChat />
        <Toaster theme="dark" position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
